// Full-screen placeholder for a scheduled interstitial ad (Open Beta / preview mode).

import React, { useEffect, useRef, useState } from "react";
import { INTERSTITIAL_AD_PREVIEW_DURATION_SEC } from "../utils/periodicInterstitialAds";

const InterstitialAdPreviewModal = ({
    open = false,
    onComplete,
    durationSec = INTERSTITIAL_AD_PREVIEW_DURATION_SEC,
}) => {
    const [secondsLeft, setSecondsLeft] = useState(durationSec);
    const completedRef = useRef(false);
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        if (!open) {
            completedRef.current = false;
            setSecondsLeft(durationSec);
            return undefined;
        }

        completedRef.current = false;
        setSecondsLeft(durationSec);
        const startedAt = Date.now();
        const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            const next = Math.max(0, durationSec - elapsed);
            setSecondsLeft(next);
            if (next <= 0 && !completedRef.current) {
                completedRef.current = true;
                clearInterval(timer);
                if (typeof onCompleteRef.current === "function") {
                    onCompleteRef.current();
                }
            }
        }, 200);

        return () => clearInterval(timer);
    }, [open, durationSec]);

    if (!open) {
        return null;
    }

    const progress =
        durationSec > 0
            ? Math.min(
                  100,
                  Math.round(((durationSec - secondsLeft) / durationSec) * 100)
              )
            : 100;
    const canClose = secondsLeft <= 0;

    return (
        <div
            className="rewardedAdPreview interstitialAdPreview"
            role="dialog"
            aria-modal="true"
            aria-labelledby="interstitialAdPreviewTitle"
        >
            <div className="rewardedAdPreview__frame">
                <div className="rewardedAdPreview__topBar">
                    <span className="rewardedAdPreview__adBadge">Ad</span>
                    <span className="rewardedAdPreview__countdown">
                        {canClose ? "Continue" : `Closing in ${secondsLeft}s`}
                    </span>
                </div>

                <div className="rewardedAdPreview__creative" aria-hidden="true">
                    <div className="rewardedAdPreview__creativeInner">
                        <div className="rewardedAdPreview__brand">Sponsored</div>
                        <div
                            id="interstitialAdPreviewTitle"
                            className="rewardedAdPreview__headline"
                        >
                            Memory Mix-Up — Open Beta
                        </div>
                        <p className="rewardedAdPreview__copy">
                            This is a preview of where a full-screen interstitial
                            would appear. Real ads arrive with AdMob later.
                        </p>
                    </div>
                </div>

                <div
                    className="rewardedAdPreview__progressTrack"
                    aria-hidden="true"
                >
                    <div
                        className="rewardedAdPreview__progressFill"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <div className="rewardedAdPreview__footer">
                    <p className="rewardedAdPreview__rewardHint">
                        {canClose
                            ? "Thanks for playing!"
                            : "Interstitial ads help support the game."}
                    </p>
                    {canClose ? (
                        <button
                            type="button"
                            className="interstitialAdPreview__continue"
                            onClick={() => {
                                if (typeof onCompleteRef.current === "function") {
                                    onCompleteRef.current();
                                }
                            }}
                        >
                            Continue
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default InterstitialAdPreviewModal;
